import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { DatePicker, Image, Upload as AntUpload } from "antd";
import Checkbox from "antd/es/checkbox/Checkbox";
import { Eye, FileText, GraduationCap, Plus, Save, Trash, UploadCloudIcon } from "lucide-react";
import dayjs from "dayjs";

import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { ConfirmPopupModal } from "../../../components/ui/dialogModal";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_CONFIRM_PERMANENT, EMPLOYEE_QUALIFICATION_DOC } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";

const createEmptyDocument = () => ({
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: null,
    document_name: "",
    document_description: "",
    document_date: null,
    is_verified: false,
    hard_copy_collected: false,
    document_file: [],
    dirty: true,
});

const mapApiDocument = (item) => ({
    key: `saved-${item.id}`,
    id: item.id,
    document_name: item.document_name || "",
    document_description: item.document_description || "",
    document_date: item.document_date ? dayjs(item.document_date) : null,
    is_verified: Boolean(item.is_verified),
    hard_copy_collected: Boolean(item.hard_copy_collected),
    document_file: item.document_url ? [item.document_url] : [],
    dirty: false,
});

const QualificationDocumentContent = forwardRef(({ employeeID }, ref) => {
    const { fetch, create, update, remove } = useApiMethods();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [confirmation, setConfirmation] = useState({
        is_permanent: false,
        actual_date_of_joining: null,
        employee_type_name: null,
    });
    const [confirmPermanentOpen, setConfirmPermanentOpen] = useState(false);
    const [confirmingPermanent, setConfirmingPermanent] = useState(false);

    const loadDocuments = async () => {
        if (!employeeID) return;
        setLoading(true);
        try {
            const apiData = await fetch(EMPLOYEE_QUALIFICATION_DOC, { employee_id: employeeID });
            if (apiData?.status === "success") {
                setDocuments((apiData.data || []).map(mapApiDocument));
                if (apiData.confirmation) {
                    setConfirmation({
                        is_permanent: Boolean(apiData.confirmation.is_permanent),
                        actual_date_of_joining: apiData.confirmation.actual_date_of_joining || null,
                        employee_type_name: apiData.confirmation.employee_type_name || null,
                    });
                }
            } else {
                ToastMsg("error", apiData?.message || "Failed to load qualification documents");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employeeID) {
            loadDocuments();
        }
        // fetch from useApiMethods is not referentially stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID]);

    const handleFieldChange = (key, field, value) => {
        setDocuments((prev) => prev.map((doc) => (
            doc.key === key ? { ...doc, [field]: value, dirty: true } : doc
        )));
    };

    const handleNewDocument = () => {
        setDocuments((prev) => [...prev, createEmptyDocument()]);
    };

    const handleRemoveUnsaved = (key) => {
        setDocuments((prev) => prev.filter((doc) => doc.key !== key));
    };

    const validateDocument = (doc, index) => {
        if (!doc.document_name?.trim()) {
            return `Document name is required for document ${index + 1}`;
        }
        if (!doc.document_date) {
            return `Date is required for document ${index + 1}`;
        }
        const hasFile = Array.isArray(doc.document_file) && doc.document_file.length > 0;
        if (!doc.id && !hasFile) {
            return `Please upload a file for document ${index + 1}`;
        }
        return null;
    };

    const persistDocument = async (doc) => {
        const payload = new FormData();
        payload.append("employee_id", employeeID);
        payload.append("document_name", doc.document_name.trim());
        payload.append("document_description", (doc.document_description || "").trim());
        payload.append("document_date", doc.document_date.format("YYYY-MM-DD"));
        payload.append("is_verified", doc.is_verified ? "true" : "false");
        payload.append("hard_copy_collected", doc.hard_copy_collected ? "true" : "false");

        const file = doc.document_file?.[0];
        if (file instanceof File) {
            payload.append("document", file);
        }

        if (doc.id) {
            payload.append("id", doc.id);
            return update(`/${EMPLOYEE_QUALIFICATION_DOC}`, payload, true);
        }
        return create(`/${EMPLOYEE_QUALIFICATION_DOC}`, payload, true);
    };

    const handleSave = async () => {
        const dirtyDocs = documents.filter((doc) => doc.dirty);
        if (dirtyDocs.length === 0) {
            return true;
        }

        for (let index = 0; index < documents.length; index += 1) {
            const doc = documents[index];
            if (!doc.dirty) continue;
            const error = validateDocument(doc, index);
            if (error) {
                ToastMsg("error", error);
                return false;
            }
        }

        setSaving(true);
        try {
            for (const doc of dirtyDocs) {
                const response = await persistDocument(doc);
                if (response?.status === "fail") {
                    ToastMsg("error", response.message || "Failed to save qualification document");
                    return false;
                }
            }
            ToastMsg("success", "Qualification documents saved successfully");
            await loadDocuments();
            return true;
        } catch (error) {
            ToastMsg("error", error.message || "An error occurred while saving");
            return false;
        } finally {
            setSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({
        handleSave,
    }));

    const handlePermanentToggle = (checked) => {
        if (!checked) {
            return;
        }
        if (confirmation.is_permanent) {
            return;
        }
        setConfirmPermanentOpen(true);
    };

    const handleConfirmPermanent = async () => {
        setConfirmingPermanent(true);
        try {
            const response = await create(`/${EMPLOYEE_CONFIRM_PERMANENT}`, {
                employee_id: employeeID,
            });
            if (response?.status === "success") {
                if (response.confirmation) {
                    setConfirmation(response.confirmation);
                } else {
                    setConfirmation((prev) => ({
                        ...prev,
                        is_permanent: true,
                        actual_date_of_joining: dayjs().format("YYYY-MM-DD"),
                    }));
                }
                setConfirmPermanentOpen(false);
                ToastMsg("success", response.message || "Employee marked as permanent");
            } else {
                ToastMsg("error", response?.message || "Failed to mark employee as permanent");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to mark employee as permanent");
        } finally {
            setConfirmingPermanent(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget?.id) return;
        setDeleting(true);
        try {
            const response = await remove(`/${EMPLOYEE_QUALIFICATION_DOC}?id=${deleteTarget.id}`);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Document deleted successfully");
                setDeleteTarget(null);
                await loadDocuments();
            } else {
                ToastMsg("error", response?.message || "Failed to delete document");
            }
        } catch (error) {
            ToastMsg("error", error.message || "An error occurred while deleting");
        } finally {
            setDeleting(false);
        }
    };

    if (loading && documents.length === 0) {
        return (
            <div className="h-[calc(100vh-260px)] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    const hasDirty = documents.some((doc) => doc.dirty);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-start gap-4 mb-2">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Qualification Documents
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Upload degree, licence, and other qualification documents. Add as many as needed.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                            <span className={cn(
                                "text-sm whitespace-nowrap",
                                confirmation.is_permanent
                                    ? "font-medium text-gray-900 dark:text-white"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                Permanent employee
                            </span>
                            <VerifiedToggle
                                checked={Boolean(confirmation.is_permanent)}
                                onChange={handlePermanentToggle}
                            />
                        </div>
                        {confirmation.actual_date_of_joining && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Actual DOJ: {dayjs(confirmation.actual_date_of_joining).format("DD-MM-YYYY")}
                            </span>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleNewDocument}
                        className="flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        New Document
                    </Button>
                </div>
            </div>

            {documents.length === 0 ? (
                <div className="text-center py-16 text-gray-500 dark:text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No qualification documents added yet</p>
                    <p className="text-sm mb-4">Use New Document to add a certificate or registration</p>
                    <Button type="button" variant="outline" onClick={handleNewDocument} className="inline-flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        New Document
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {documents.map((doc, index) => (
                        <div
                            key={doc.key}
                            className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <GraduationCap className="w-4 h-4 text-sidebar-primary" />
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                        Document {index + 1}
                                        {doc.document_name ? ` — ${doc.document_name}` : ""}
                                    </span>
                                    {doc.id && !doc.dirty && (
                                        <span className={cn(
                                            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                                            doc.is_verified
                                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                                : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                        )}>
                                            {doc.is_verified ? "Verified" : "Not Verified"}
                                        </span>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        if (doc.id) {
                                            setDeleteTarget(doc);
                                        } else {
                                            handleRemoveUnsaved(doc.key);
                                        }
                                    }}
                                    className="p-2 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                                >
                                    <Trash className="w-4 h-4" />
                                </Button>
                            </div>

                            <div className="flex items-end gap-3 overflow-x-auto pb-1">
                                <FormField label="Document Name" required className="w-64 shrink-0">
                                    <Input
                                        value={doc.document_name}
                                        onChange={(e) => handleFieldChange(doc.key, "document_name", e.target.value)}
                                        placeholder="e.g. Degree Certificate"
                                        className="h-9 bg-background"
                                    />
                                </FormField>

                                <FormField label="Date" required className="w-48 shrink-0">
                                    <DatePicker
                                        allowClear
                                        format="DD-MM-YYYY"
                                        value={doc.document_date}
                                        onChange={(date) => handleFieldChange(doc.key, "document_date", date)}
                                        className="w-full"
                                        placeholder="Select date"
                                    />
                                </FormField>

                                <FormField label="Document Description" className="w-80 shrink-0">
                                    <Input
                                        value={doc.document_description}
                                        onChange={(e) => handleFieldChange(doc.key, "document_description", e.target.value)}
                                        placeholder="Enter description"
                                        className="h-9 bg-background"
                                    />
                                </FormField>

                                <FormField label="Verification" className="shrink-0">
                                    <div className="flex items-center gap-2 h-9">
                                        <span className={cn(
                                            "text-xs whitespace-nowrap",
                                            !doc.is_verified
                                                ? "font-medium text-gray-900 dark:text-white"
                                                : "text-gray-500 dark:text-gray-400"
                                        )}>
                                            Not Verified
                                        </span>
                                        <VerifiedToggle
                                            checked={doc.is_verified}
                                            onChange={(checked) => handleFieldChange(doc.key, "is_verified", checked)}
                                        />
                                        <span className={cn(
                                            "text-xs whitespace-nowrap",
                                            doc.is_verified
                                                ? "font-medium text-gray-900 dark:text-white"
                                                : "text-gray-500 dark:text-gray-400"
                                        )}>
                                            Verified
                                        </span>
                                    </div>
                                </FormField>

                                <FormField label="Upload Document" required={!doc.id} className="w-44 shrink-0">
                                    <DocFileUpload
                                        compact
                                        attachment={doc.document_file}
                                        onFileChange={(file) => handleFieldChange(doc.key, "document_file", file ? [file] : [])}
                                        onRemoveFile={() => handleFieldChange(doc.key, "document_file", [])}
                                    />
                                </FormField>

                                <div className="flex items-center h-9 shrink-0">
                                    <Checkbox
                                        checked={!!doc.hard_copy_collected}
                                        onChange={(e) => handleFieldChange(doc.key, "hard_copy_collected", e.target.checked)}
                                    >
                                        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                            Hard copy collected
                                        </span>
                                    </Checkbox>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="flex justify-end items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <Button
                            variant="brand"
                            disabled={saving || !hasDirty}
                            onClick={handleSave}
                            className="flex items-center gap-2 transition-colors duration-200"
                        >
                            {saving ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {saving ? "Saving..." : "Save Documents"}
                        </Button>
                    </div>
                </div>
            )}

            <ConfirmPopupModal
                open={confirmPermanentOpen}
                onClose={() => {
                    if (!confirmingPermanent) {
                        setConfirmPermanentOpen(false);
                    }
                }}
                onConfirm={handleConfirmPermanent}
                title="Confirm permanent employee"
                description={`Mark this employee as permanent? Actual date of joining will be set to today (${dayjs().format("DD-MM-YYYY")}).`}
                type="info"
                confirmLabel="OK"
                loading={confirmingPermanent}
            />

            <ConfirmPopupModal
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Delete qualification document"
                description="Are you sure you want to delete this qualification document? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={deleting}
            />
        </div>
    );
});

const FormField = ({ label, required = false, children, className }) => (
    <div className={cn("space-y-2", className)}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
        </label>
        {children}
    </div>
);

const VerifiedToggle = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800",
            checked ? "bg-sidebar" : "bg-gray-300 dark:bg-gray-600"
        )}
    >
        <span
            className={cn(
                "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                checked ? "translate-x-[22px]" : "translate-x-0.5"
            )}
        />
    </button>
);

const DocFileUpload = ({ attachment, onFileChange, onRemoveFile, compact = false }) => {
    const isFileObject = attachment && Array.isArray(attachment) && attachment.length > 0 && attachment[0] instanceof File;
    const isStringUrl = attachment && Array.isArray(attachment) && attachment.length > 0 && typeof attachment[0] === "string";
    const hasAttachment = isFileObject || isStringUrl;

    if (!hasAttachment) {
        return (
            <AntUpload
                accept=".doc,.docx,.pdf,image/*"
                maxCount={1}
                beforeUpload={() => false}
                onChange={({ fileList }) => {
                    if (fileList.length > 0 && fileList[0].originFileObj) {
                        onFileChange(fileList[0].originFileObj);
                    }
                }}
                showUploadList={false}
                className={compact ? "w-auto" : "w-full"}
            >
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(
                        "border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:bg-blue-900 dark:text-white transition-colors",
                        compact ? "h-9 px-3" : "w-full"
                    )}
                >
                    <UploadCloudIcon className="w-4 h-4 mr-2" />
                    Upload Document
                </Button>
            </AntUpload>
        );
    }

    const fileUrl = isFileObject ? URL.createObjectURL(attachment[0]) : attachment[0];
    const fileName = isFileObject
        ? attachment[0].name
        : (String(attachment[0]).split("/").pop() || "Uploaded Document");
    const isImage = isFileObject
        ? attachment[0].type?.startsWith("image/")
        : /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/i.test(attachment[0]);

    return (
        <div className={cn(
            "flex justify-between items-center gap-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-background",
            compact ? "p-1.5" : "p-3 gap-3"
        )}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
                {isImage ? (
                    <Image src={fileUrl} alt="preview" width={40} height={40} className="rounded-md object-cover" />
                ) : (
                    <FileText className="w-8 h-8 text-blue-500 flex-shrink-0" />
                )}
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {fileName}
                </span>
            </div>
            <div className="flex gap-1">
                <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/20"
                >
                    <Eye className="w-4 h-4" />
                </a>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onRemoveFile}
                    className="p-2 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                >
                    <Trash className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

QualificationDocumentContent.displayName = "QualificationDocumentContent";

export default QualificationDocumentContent;
